import { Box, FlexLayout } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { Text } from '@deity-io/portal-ui';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { MappingActionTypes, MappingContext } from 'contexts/MappingContext';
import type { FC } from 'react';
import { useContext, useState } from 'react';
import { Collapsible } from '../../../Collapsible';
import AddProcessor from './AddProcessor';
import ProcessorItem from './ProcessorItem';

interface IEditProcessors {
  locked?: boolean;
  availableProcessors: string[];
  processorProperties: {};
  processors: Array<string | IPropertyBag>;
  fieldPath: string[];
  owner?: any;
  title?: string;
  configPath?: string[];
  onChange: (path: Array<string>, value: any) => void;
}
const EditProcessors: FC<IEditProcessors> = ({
  locked,
  availableProcessors,
  processorProperties,
  processors,
  fieldPath,
  owner,
  onChange,
  title = 'Processors',
  configPath = ['processors']
}) => {
  const ctx = useContext(MappingContext);

  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  function handleDragEnd(event) {
    const { active, over } = event;

    if (active.id !== over.id) {
      const sortProcessors = (processorsToSort: any[]) => arrayMove(processorsToSort, active.id - 1, over.id - 1);
      updateProcessorsList(sortProcessors(processors));
    }
    setActiveId(null);
  }

  function handleDragStart(event) {
    setActiveId(event.active.id);
  }

  const updateProcessorsList = (updatedProcessors: any[]) => {
    if (fieldPath) {
      ctx?.dispatch({
        type: MappingActionTypes.UPDATE_DEFAULT,
        payload: {
          path: fieldPath,
          property: configPath,
          value: updatedProcessors
        }
      });
    }
  };

  return (
    <>
      {availableProcessors.length ? (
        <Collapsible title={title} itemCount={processors.length}>
          <Box px="xs" my="xs">
            <Text>Set one or more constraint types for this field.</Text>
          </Box>
          {!locked && (
            <AddProcessor
              processors={availableProcessors}
              onProcessorAdd={val => {
                onChange([...configPath, `${processors.length}`], { type: val });
              }}
            />
          )}
          <FlexLayout
            width="full"
            pb="xxxl"
            css={{
              overflowX: 'hidden'
            }}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={processors.map((val, index) => ({ id: `${index}` }))}
                strategy={verticalListSortingStrategy}
              >
                {processors.map((processor, i) => {
                  const processorType = typeof processor === 'string' ? processor : processor.type;

                  return (
                    <ProcessorItem
                      locked={locked}
                      key={i} // eslint-disable-line react/no-array-index-key
                      processorProperties={processorProperties[processorType]}
                      persistedValue={processor}
                      type={processorType}
                      index={(i + 1).toString()}
                      owner={owner}
                      onRemove={index =>
                        updateProcessorsList(processors.filter(p => p !== processors[parseInt(index, 10) - 1]))
                      }
                      onValueChange={(key, value) => {
                        onChange([...configPath, `${i}`, ...key], value);
                      }}
                    />
                  );
                })}
              </SortableContext>
              <DragOverlay>
                {activeId ? (
                  <Box
                    bg="white"
                    css={{
                      height: '142px',
                      cursor: 'grab'
                    }}
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          </FlexLayout>
        </Collapsible>
      ) : null}
    </>
  );
};

export default EditProcessors;
